import { useState } from "react";
import {
  PuzzlePiece,
  MagnifyingGlass,
  EnvelopeSimple,
  ArrowRight,
  Play,
  Lock,
  Fire,
  Star,
  CheckCircle,
  Trophy,
} from "@phosphor-icons/react";
import { SectionShell } from "../Primitives";
import { BloomButton } from "../ui/BloomButton";
import { BloomCard } from "../ui/BloomCard";
import { BloomInput } from "../ui/BloomInput";
import { BloomBadge } from "../ui/BloomBadge";
import { BloomProgress } from "../ui/BloomProgress";
import { Mascot } from "../Mascot";

function Block({ title, children }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft md:p-8">
      <h3 className="mb-6 font-display text-xl font-extrabold">{title}</h3>
      {children}
    </div>
  );
}

function Ring({ value = 72 }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  return (
    <svg width="92" height="92" viewBox="0 0 92 92" className="-rotate-90">
      <circle cx="46" cy="46" r={r} className="fill-none stroke-muted" strokeWidth="9" />
      <circle
        cx="46"
        cy="46"
        r={r}
        className="fill-none stroke-primary"
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c - (value / 100) * c}
      />
    </svg>
  );
}

export function ComponentsSection() {
  const [email, setEmail] = useState("");

  return (
    <SectionShell
      id="components"
      eyebrow="Components"
      eyebrowIcon={PuzzlePiece}
      title="Core components, built to spec"
      description="The building blocks that compose every screen — fully round buttons, soft cards, friendly inputs, expressive badges and encouraging progress. Each one meets the 48px touch-target and contrast rules."
    >
      <div className="space-y-5">
        <Block title="Buttons">
          <div className="flex flex-wrap gap-3">
            <BloomButton variant="primary">Primary</BloomButton>
            <BloomButton variant="secondary">Secondary</BloomButton>
            <BloomButton variant="teal">Teal</BloomButton>
            <BloomButton variant="outline">Outline</BloomButton>
            <BloomButton variant="ghost">Ghost</BloomButton>
            <BloomButton variant="soft">Soft</BloomButton>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <BloomButton size="sm">Small</BloomButton>
            <BloomButton size="md">Medium</BloomButton>
            <BloomButton size="lg">Large</BloomButton>
            <BloomButton leftIcon={Play}>Start lesson</BloomButton>
            <BloomButton variant="outline" rightIcon={ArrowRight}>Next</BloomButton>
            <BloomButton disabled leftIcon={Lock}>Locked</BloomButton>
          </div>
        </Block>

        <div className="grid gap-5 lg:grid-cols-2">
          <Block title="Inputs">
            <div className="space-y-5">
              <BloomInput label="Your name" placeholder="e.g. Maya" />
              <BloomInput
                label="Find a lesson"
                icon={MagnifyingGlass}
                placeholder="Search topics…"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <BloomInput
                label="Grown-up email"
                icon={EnvelopeSimple}
                placeholder="parent@email.com"
                error="Please enter a valid email address."
              />
              <BloomInput label="Class code" placeholder="BLOOM-123" helper="Ask your teacher for this code." />
            </div>
          </Block>

          <Block title="Badges">
            <div className="flex flex-wrap gap-3">
              <BloomBadge variant="primary" icon={Fire}>3 day streak</BloomBadge>
              <BloomBadge variant="teal" dot>Online</BloomBadge>
              <BloomBadge variant="success" icon={CheckCircle}>Complete</BloomBadge>
              <BloomBadge variant="secondary" icon={Star}>+15 XP</BloomBadge>
              <BloomBadge variant="blue">New</BloomBadge>
              <BloomBadge variant="info">Hint</BloomBadge>
              <BloomBadge variant="warning">Review</BloomBadge>
              <BloomBadge variant="neutral">Year 4</BloomBadge>
            </div>
            <div className="mt-7">
              <h3 className="mb-4 font-display text-base font-extrabold">Progress</h3>
              <div className="flex items-center gap-6">
                <div className="flex-1 space-y-4">
                  <BloomProgress value={72} color="primary" label="Reading" showValue />
                  <BloomProgress value={45} color="teal" label="Numbers" showValue />
                  <BloomProgress value={90} color="blue" label="Science" showValue />
                </div>
                <div className="relative flex flex-col items-center">
                  <Ring value={72} />
                  <span className="absolute top-7 font-display text-xl font-black">72%</span>
                  <span className="mt-1 text-xs font-bold text-muted-foreground">Today</span>
                </div>
              </div>
            </div>
          </Block>
        </div>

        <Block title="Components in concert">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <BloomCard interactive>
              <div className="flex items-start justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/12 text-primary-strong">
                  <Star size={26} weight="fill" />
                </span>
                <BloomBadge variant="secondary" icon={Star}>+15 XP</BloomBadge>
              </div>
              <h4 className="mt-4 font-display text-xl font-extrabold">Reading Quest</h4>
              <p className="mt-1 font-medium text-muted-foreground">Chapter 3 · Sounds &amp; words</p>
              <div className="mt-4"><BloomProgress value={60} color="primary" /></div>
              <BloomButton className="mt-5" fullWidth rightIcon={ArrowRight}>Continue</BloomButton>
            </BloomCard>

            <BloomCard interactive>
              <div className="flex items-start justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal/15 text-teal-ink">
                  <CheckCircle size={26} weight="fill" />
                </span>
                <BloomBadge variant="success">Done</BloomBadge>
              </div>
              <h4 className="mt-4 font-display text-xl font-extrabold">Counting Coins</h4>
              <p className="mt-1 font-medium text-muted-foreground">You got 9 of 10. Brilliant!</p>
              <div className="mt-4"><BloomProgress value={100} color="teal" /></div>
              <BloomButton className="mt-5" variant="outline" fullWidth>Review</BloomButton>
            </BloomCard>

            <BloomCard className="flex flex-col items-center justify-center bg-gradient-to-b from-accent/70 to-card text-center">
              <Mascot size={96} />
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary/12 px-3 py-1.5 text-sm font-bold text-primary-strong">
                <Trophy size={16} weight="fill" /> Daily goal reached
              </span>
              <p className="mt-2 font-medium text-muted-foreground">See you tomorrow!</p>
            </BloomCard>
          </div>
        </Block>
      </div>
    </SectionShell>
  );
}

export default ComponentsSection;
